import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  Rows,
  SquaresFour,
  Wrench,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { DensityToggle, useDensity } from "@/components/DensityToggle";
import { SearchSelect } from "@/components/SearchSelect";
import {
  ServiceCards,
  ServiceRows,
  groupByCategory,
} from "@/components/ServicesViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  name: "",
  taxable: false,
  apply_discount: true,
  description: "",
  alt_description: "",
  type: "",
  category: "",
  unit: "EA",
  cost_estimate: 0,
  installer_app: false,
  vendor_delivered: false,
  active: true,
};

const SectionTitle = ({ children }) => (
  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-300">
    {children}
  </div>
);

export default function Services() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ types: [], categories: [], units: [] });
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(
    () => localStorage.getItem("bg_services_view") || "list",
  );
  const [density, setDensity, compact] = useDensity();
  useEffect(() => localStorage.setItem("bg_services_view", view), [view]);

  const load = useCallback(async () => {
    const [list, m] = await Promise.all([
      api.get("/services"),
      api.get("/services/meta"),
    ]);
    setItems(list.data);
    setMeta(m.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = items.filter((s) =>
    `${s.name} ${s.category} ${s.type} ${s.description} ${s.alt_description}`
      .toLowerCase()
      .includes(q.trim().toLowerCase()),
  );

  const groups = groupByCategory(filtered);
  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ ...EMPTY, ...s });
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Give the service a name");
      return;
    }
    if (!form.category.trim()) {
      toast.error("Pick a category");
      return;
    }
    setBusy(true);
    try {
      const body = { ...form, cost_estimate: Number(form.cost_estimate) || 0 };
      const { data } = editing
        ? await api.put(`/services/${editing}`, body)
        : await api.post("/services", body);
      toast.success(`${data.name} ${editing ? "updated" : "created"}`);
      setForm(null);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save that service",
      );
    }
    setBusy(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete “${s.name}”?`)) return;
    await api.delete(`/services/${s.id}`);
    toast.success("Service deleted");
    load();
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Services" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="services-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Services
              </h1>
              <p
                className="mt-1 text-sm text-zinc-500"
                data-testid="services-count"
              >
                {items.length} service{items.length === 1 ? "" : "s"} priced for
                quote lines
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2 border-t border-zinc-800 pt-4">
            <Button
              data-testid="add-service-button"
              onClick={() => {
                setEditing(null);
                setForm({ ...EMPTY });
              }}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> Create Service
            </Button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <MagnifyingGlass
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <Input
              data-testid="service-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search services, categories, aliases…"
              className="h-12 border-zinc-800 bg-zinc-900 pl-10"
            />
          </div>
          <div className="flex shrink-0 overflow-hidden rounded-md border border-zinc-800">
            {[
              ["list", Rows],
              ["grid", SquaresFour],
            ].map(([k, Icon]) => (
              <button
                key={k}
                type="button"
                data-testid={`services-view-${k}`}
                onClick={() => setView(k)}
                title={`${k} view`}
                className={`flex h-12 items-center gap-2 px-3 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  view === k
                    ? "bg-indigo-500 text-white"
                    : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                <Icon size={16} weight="bold" />
                <span className="hidden sm:inline">{k}</span>
              </button>
            ))}
          </div>
          <DensityToggle density={density} onChange={setDensity} />
        </div>

        {filtered.length === 0 ? (
          <div
            className="mt-4 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            data-testid="services-empty"
          >
            <Wrench size={26} weight="bold" className="mx-auto text-zinc-700" />
            <p className="mt-3 text-sm text-zinc-400">
              {items.length === 0
                ? "No services yet — create one and it becomes pickable on any quote."
                : "No services match that search."}
            </p>
          </div>
        ) : view === "grid" ? (
          <ServiceCards
            groups={groups}
            compact={compact}
            onEdit={startEdit}
            onDelete={remove}
          />
        ) : (
          <ServiceRows
            groups={groups}
            compact={compact}
            onEdit={startEdit}
            onDelete={remove}
          />
        )}
      </main>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent
          className="max-h-[92vh] overflow-y-auto border-zinc-800 bg-zinc-900 sm:max-w-lg"
          data-testid="service-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit service" : "Create Service"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <SectionTitle>Main details</SectionTitle>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Service name *
              </Label>
              <Input
                data-testid="service-name-input"
                value={form?.name || ""}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="Undermount sink cut-out"
                className="h-12 border-zinc-800 bg-zinc-950"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Service description
              </Label>
              <Textarea
                data-testid="service-description-input"
                value={form?.description || ""}
                maxLength={10000}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="Enter a description…"
                className="min-h-[104px] border-zinc-800 bg-zinc-950"
              />
              <div className="text-right text-[10px] text-zinc-500">
                {(form?.description || "").length} / 10,000
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Alternative description
              </Label>
              <Textarea
                data-testid="service-alt-input"
                value={form?.alt_description || ""}
                maxLength={1000}
                onChange={(e) => set({ alt_description: e.target.value })}
                placeholder="Alias your customer uses — will appear in search"
                className="min-h-[72px] border-zinc-800 bg-zinc-950"
              />
              <div className="text-right text-[10px] text-zinc-500">
                {(form?.alt_description || "").length} / 1,000
              </div>
            </div>

            <SectionTitle>Additional details</SectionTitle>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Type
              </Label>
              <SearchSelect
                testId="service-type-select"
                value={form?.type || ""}
                options={(meta.types || []).map((t) => ({
                  value: t,
                  label: t,
                }))}
                allowCustom
                placeholder="Choose type"
                searchPlaceholder="Search or type a new one…"
                onChange={(v) => set({ type: v })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Category *
              </Label>
              <SearchSelect
                testId="service-category-select"
                value={form?.category || ""}
                options={(meta.categories || []).map((c) => ({
                  value: c,
                  label: c,
                }))}
                allowCustom
                placeholder="Choose category"
                searchPlaceholder="Search or type a new one…"
                onChange={(v) => set({ category: v })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Unit of measure *
                </Label>
                <SearchSelect
                  testId="service-unit-select"
                  value={form?.unit || "EA"}
                  options={(meta.units || []).map((u) => ({
                    value: u,
                    label: u,
                  }))}
                  placeholder="Select"
                  onChange={(v) => set({ unit: v })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Cost estimate
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500">
                    $
                  </span>
                  <Input
                    data-testid="service-cost-input"
                    type="number"
                    step="0.01"
                    value={form?.cost_estimate ?? 0}
                    onChange={(e) => set({ cost_estimate: e.target.value })}
                    className="h-12 border-zinc-800 bg-zinc-950 pl-7 font-mono-data"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              data-testid="service-cancel"
              variant="outline"
              onClick={() => setForm(null)}
              className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              data-testid="service-save"
              disabled={busy}
              onClick={submit}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Saving…" : editing ? "Save service" : "Create service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
